import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, Key, Save, Plus, X, Shield, AlertCircle, 
  Lock, Smartphone, Eye, EyeOff, Check, Copy, RefreshCw
} from 'lucide-react';
import api from '../services/api';

export default function Settings() {
  // Password Arsenal
  const [globalPasswords, setGlobalPasswords] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [loadingPasswords, setLoadingPasswords] = useState(true);
  const [savingPasswords, setSavingPasswords] = useState(false);
  
  // User Password Change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQR, setTwoFAQR] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  
  // Messages
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadPasswords();
    load2FAStatus();
  }, []);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // ===== PASSWORD ARSENAL =====
  const loadPasswords = async () => {
    try {
      const res = await api.get('/passwords/get');
      if (res.data.success) {
        setGlobalPasswords(res.data.passwords);
      }
    } catch (err) {
      console.error('Load passwords error:', err);
    } finally {
      setLoadingPasswords(false);
    }
  };

  const handleAddPassword = () => {
    if (newPassword.trim() && !globalPasswords.includes(newPassword.trim())) {
      setGlobalPasswords([...globalPasswords, newPassword.trim()]);
      setNewPassword('');
    }
  };

  const handleRemovePassword = (index) => {
    if (globalPasswords.length > 1) {
      setGlobalPasswords(globalPasswords.filter((_, i) => i !== index));
    } else {
      showMessage('error', 'En az bir şifre gerekli!');
    }
  };

  const handleSavePasswords = async () => {
    try {
      setSavingPasswords(true);
      await api.post('/passwords/update', { passwords: globalPasswords });
      showMessage('success', 'Şifreler kaydedildi!');
    } catch (err) {
      showMessage('error', 'Kayıt hatası!');
    } finally {
      setSavingPasswords(false);
    }
  };

  // ===== USER PASSWORD CHANGE =====
  const handleChangePassword = async () => {
    if (!currentPassword || !newUserPassword || !confirmPassword) {
      showMessage('error', 'Tüm alanları doldurun!');
      return;
    }
    if (newUserPassword !== confirmPassword) {
      showMessage('error', 'Yeni şifreler eşleşmiyor!');
      return;
    }
    if (newUserPassword.length < 6) {
      showMessage('error', 'Şifre en az 6 karakter olmalı!');
      return;
    }

    try {
      setChangingPassword(true);
      const res = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newUserPassword
      });
      if (res.data.success) {
        showMessage('success', 'Şifre değiştirildi!');
        setCurrentPassword('');
        setNewUserPassword('');
        setConfirmPassword('');
      } else {
        showMessage('error', res.data.error || 'Hata oluştu!');
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Şifre değiştirilemedi!');
    } finally {
      setChangingPassword(false);
    }
  };

  // ===== 2FA =====
  const load2FAStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setTwoFAEnabled(res.data.enabled);
    } catch (err) {
      console.error('2FA status error:', err);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setSetting2FA(true);
      const res = await api.post('/auth/2fa/setup');
      if (res.data.success) {
        setTwoFASecret(res.data.secret);
        setTwoFAQR(res.data.qr_code);
        setShow2FASetup(true);
      }
    } catch (err) {
      showMessage('error', '2FA kurulumu başlatılamadı!');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      showMessage('error', '6 haneli kod girin!');
      return;
    }
    try {
      setSetting2FA(true);
      const res = await api.post('/auth/2fa/verify', { code: verifyCode });
      if (res.data.success) {
        setTwoFAEnabled(true);
        setShow2FASetup(false);
        setVerifyCode('');
        showMessage('success', '2FA aktifleştirildi!');
      } else {
        showMessage('error', 'Kod geçersiz!');
      }
    } catch (err) {
      showMessage('error', 'Doğrulama hatası!');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('2FA\'yı devre dışı bırakmak istediğinize emin misiniz?')) return;
    try {
      const res = await api.post('/auth/2fa/disable');
      if (res.data.success) {
        setTwoFAEnabled(false);
        showMessage('success', '2FA devre dışı bırakıldı!');
      }
    } catch (err) {
      showMessage('error', '2FA devre dışı bırakılamadı!');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showMessage('success', 'Kopyalandı!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl shadow-lg">
          <SettingsIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Ayarlar</h1>
          <p className="text-gray-400 text-sm">Sistem ve güvenlik ayarları</p>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
          'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Password Arsenal */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Key className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Global Password Arsenal</h2>
              <p className="text-sm text-gray-400">Bulk testlerde kullanılacak şifreler</p>
            </div>
          </div>
          <button
            onClick={handleSavePasswords}
            disabled={savingPasswords}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 rounded-lg text-white font-medium transition-colors"
          >
            <Save className="w-4 h-4" />
            {savingPasswords ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>

        {/* Add Password */}
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddPassword()}
            placeholder="Yeni şifre ekle..."
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg py-2 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
          />
          <button
            onClick={handleAddPassword}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-white font-medium"
          >
            <Plus className="w-4 h-4" /> Ekle
          </button>
        </div>

        {/* Password List */}
        <div className="flex flex-wrap gap-2">
          {globalPasswords.map((pwd, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
              <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/20 text-cyan-400 text-xs rounded">{idx + 1}</span>
              <span className="text-white font-mono">{pwd}</span>
              <button onClick={() => handleRemovePassword(idx)} className="text-gray-500 hover:text-red-400">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Change User Password */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Lock className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Hesap Şifresi Değiştir</h2>
            <p className="text-sm text-gray-400">Giriş şifrenizi değiştirin</p>
          </div>
        </div>

        <div className="grid gap-4 max-w-md">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Mevcut Şifre</label>
            <div className="relative">
              <input
                type={showPasswords ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-4 pr-10 text-white focus:outline-none focus:border-cyan-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Yeni Şifre</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Yeni Şifre (Tekrar)</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
          <button
            onClick={handleChangePassword}
            disabled={changingPassword}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-700 rounded-lg text-white font-medium transition-colors"
          >
            {changingPassword ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {changingPassword ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
          </button>
        </div>
      </div>

      {/* 2FA */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Smartphone className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">İki Faktörlü Doğrulama (2FA)</h2>
              <p className="text-sm text-gray-400">Google Authenticator ile ekstra güvenlik</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            twoFAEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'
          }`}>
            {twoFAEnabled ? 'Aktif' : 'Pasif'}
          </div>
        </div>

        {!twoFAEnabled && !show2FASetup && (
          <button
            onClick={handleSetup2FA}
            disabled={setting2FA}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 rounded-lg text-white font-medium"
          >
            <Shield className="w-4 h-4" />
            {setting2FA ? '2FA Kuruluyor...' : '2FA Aktifleştir'}
          </button>
        )}

        {show2FASetup && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400 mb-3">
                1. Google Authenticator veya benzer bir uygulama açın<br />
                2. QR kodu tarayın veya aşağıdaki kodu manuel girin<br />
                3. Uygulamadaki 6 haneli kodu aşağıya yazın
              </p>
              
              {twoFAQR && (
                <div className="flex justify-center mb-4">
                  <img src={twoFAQR} alt="2FA QR Code" className="w-48 h-48 bg-white p-2 rounded-lg" />
                </div>
              )}
              
              <div className="flex items-center gap-2 mb-4">
                <code className="flex-1 bg-gray-900 px-3 py-2 rounded text-cyan-400 font-mono text-sm">{twoFASecret}</code>
                <button onClick={() => copyToClipboard(twoFASecret)} className="p-2 bg-gray-700 rounded hover:bg-gray-600">
                  <Copy className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 haneli kod"
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-lg py-2 px-4 text-white text-center font-mono text-xl tracking-widest focus:outline-none focus:border-purple-500"
                  maxLength={6}
                />
                <button
                  onClick={handleVerify2FA}
                  disabled={setting2FA || verifyCode.length !== 6}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 rounded-lg text-white font-medium"
                >
                  Doğrula
                </button>
              </div>
            </div>
            <button
              onClick={() => setShow2FASetup(false)}
              className="text-gray-500 hover:text-gray-400 text-sm"
            >
              İptal
            </button>
          </div>
        )}

        {twoFAEnabled && (
          <button
            onClick={handleDisable2FA}
            className="flex items-center gap-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 font-medium"
          >
            <X className="w-4 h-4" />
            2FA Devre Dışı Bırak
          </button>
        )}
      </div>

      {/* System Info */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gray-700 rounded-lg">
            <Shield className="w-5 h-5 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Sistem Bilgileri</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="text-sm text-gray-500">Versiyon</div>
            <div className="text-white font-medium">v9.0 Production</div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-3">
            <div className="text-sm text-gray-500">Veritabanı</div>
            <div className="text-white font-medium">MySQL (pose_monster)</div>
          </div>
        </div>
      </div>
    </div>
  );
}
